import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Instagram, Facebook, Settings2, Loader } from 'lucide-react';
import type { MetaUnavailableReason } from '@/lib/hooks/useMetaChannels';
import { CHANNEL_LABEL } from './metaConversationHelpers';

interface MetaUnavailableStateProps {
  channel: 'instagram' | 'messenger';
  /** null = configurazione ancora in caricamento. */
  reason: MetaUnavailableReason | null;
  canManage: boolean;
  onClose?: () => void;
}

const META_SETTINGS_URL = '/settings?tab=integrations-hub&integration=meta-ads';

/**
 * Stato "non collegato" delle schede Instagram e Messenger. Le schede restano
 * sempre visibili (decisione Luca 02/09/2026): qui si spiega cosa manca e chi
 * puo' sistemarlo, con il pulsante verso la card Meta del Marketplace quando
 * l'utente ha titolo per farlo.
 */
export function MetaUnavailableState({ channel, reason, canManage, onClose }: MetaUnavailableStateProps) {
  const navigate = useNavigate();
  const Icon = channel === 'instagram' ? Instagram : Facebook;
  const label = CHANNEL_LABEL[channel];

  const goToSettings = () => {
    onClose?.();
    navigate(META_SETTINGS_URL);
  };

  let title: string;
  let text: string;
  let action: { label: string; onClick: () => void } | null = null;

  switch (reason) {
    case null:
      title = 'Un momento';
      text = `Sto controllando la configurazione ${label}.`;
      break;
    case 'not_connected':
      title = `${label} non collegato`;
      text = `Per ricevere e rispondere ai messaggi ${label} dal CRM serve collegare l'account Meta della tua organizzazione e attivare la messaggistica.`;
      action = canManage ? { label: 'Collega Meta dal Marketplace', onClick: goToSettings } : null;
      if (!canManage) text += " Chiedi a chi amministra l'organizzazione.";
      break;
    case 'messaging_off':
      title = 'Messaggistica non attiva';
      text = `L'account Meta e' collegato, ma la ricezione dei messaggi ${label} nel CRM non e' stata attivata.`;
      action = canManage ? { label: 'Apri le impostazioni Meta', onClick: goToSettings } : null;
      if (!canManage) text += " Chiedi a chi amministra l'organizzazione.";
      break;
    case 'no_permission':
      title = 'Permesso mancante';
      text = `Il tuo ruolo non include "Vedi messaggi Instagram e Messenger". Chiedi a chi gestisce i ruoli di aggiungerlo.`;
      break;
    case 'no_pages':
    default:
      title = 'Nessuna pagina assegnata';
      text = `La messaggistica e' attiva, ma non sei assegnato a nessuna pagina Facebook: le conversazioni ${label} restano nascoste finche' un responsabile non ti aggiunge da "Chi vede le conversazioni" nelle impostazioni Meta.`;
      action = canManage ? { label: 'Gestisci gli accessi', onClick: goToSettings } : null;
      break;
  }

  return (
    <div className="flex-1 flex items-center justify-center bg-[#FBFBFA] dark:bg-[#191919] px-6">
      <div className="max-w-md text-center">
        {reason === null ? (
          <Loader className="h-8 w-8 text-[#c9c9c5] mx-auto mb-3 animate-spin" />
        ) : (
          <Icon className="h-10 w-10 text-[#c9c9c5] mx-auto mb-3" />
        )}
        <h3 className="text-base font-semibold text-[#37352F] dark:text-white mb-1">{title}</h3>
        <p className="text-sm text-[#787774] leading-relaxed">{text}</p>
        {action && (
          <button
            type="button"
            onClick={action.onClick}
            className="mt-4 inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-[#2383e2] text-white text-sm font-medium hover:bg-[#1b6cc2] transition-colors"
          >
            <Settings2 className="h-4 w-4" />
            {action.label}
          </button>
        )}
      </div>
    </div>
  );
}
